import React from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  IL_COLORS,
  formatCurrency,
  formatSignedPercent,
} from "../../../../constants/investmentLeague";

const COLS = [
  { key: "rank",          label: "Rank",           align: "left"  },
  { key: "unit",          label: "Investor",       align: "left"  },
  { key: "invested",      label: "Invested",       align: "right" },
  { key: "q1Credit",      label: "Q1 Credit",      align: "right" },
  { key: "current",       label: "Current Value",  align: "right" },
  { key: "allTimeReturn", label: "All-Time Return",align: "right" },
  { key: "qtrReturn",     label: "Qtr Return",     align: "right" },
  { key: "draw",          label: "Draw",           align: "right" },
];

const cellSx = (align) => ({
  textAlign:  align,
  py:         1.5,
  px:         { xs: 1.5, md: 2 },
  fontSize:   "12.5px",
  whiteSpace: "nowrap",
});

const RANK_BADGE = {
  1: { bg: "#F5C842", color: "#5C4A00" },
  2: { bg: "#C8CDD4", color: "#3D4450" },
  3: { bg: "#D4956A", color: "#4A3020" },
};

const RankBadge = ({ rank, t }) => {
  const style = RANK_BADGE[rank] ?? { bg: t.surface3, color: t.text2 };

  return (
    <Box
      sx={{
        width:          28,
        height:         28,
        borderRadius:   "50%",
        background:     style.bg,
        color:          style.color,
        display:        "flex",
        alignItems:     "center",
        justifyContent: "center",
        fontSize:       "12px",
        fontWeight:     700,
        flexShrink:     0,
      }}
    >
      {rank}
    </Box>
  );
};

const returnColor = (value) =>
  value >= 0 ? IL_COLORS.positive : IL_COLORS.negative;

const rowBackground = (row, t) => {
  if (row.highlight === "top")    return IL_COLORS.positiveBg;
  if (row.highlight === "bottom") return IL_COLORS.negativeBg;
  return "transparent";
};

const LeagueStandingsTable = ({ rows = [], totals, onUnitClick }) => {
  const theme = useTheme();
  const t     = theme.custom;

  return (
    <Box sx={{ overflowX: "auto" }}>
      <Box
        component="table"
        sx={{
          width:          "100%",
          minWidth:       "900px",
          borderCollapse: "collapse",
        }}
      >
        <Box component="thead">
          <Box component="tr" sx={{ background: t.surface2 }}>
            {COLS.map((col) => (
              <Box
                component="th"
                key={col.key}
                sx={{
                  ...cellSx(col.align),
                  fontSize:      "10px",
                  fontWeight:    600,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color:         t.text3,
                  borderBottom:  `1px solid ${t.border}`,
                }}
              >
                {col.label}
              </Box>
            ))}
          </Box>
        </Box>

        <Box component="tbody">
          {rows.map((row) => {
            const allTimeColor = returnColor(row.allTimeReturn);
            const qtrColor     = returnColor(row.qtrReturn);
            const bg           = rowBackground(row, t);

            return (
              <Box
                component="tr"
                key={row.rank}
                onClick={() => onUnitClick?.(row)}
                sx={{
                  background:   bg,
                  borderBottom: `1px solid ${t.border}`,
                  cursor:       onUnitClick ? "pointer" : "default",
                  "&:hover":    onUnitClick ? { background: bg || t.surface2 } : {},
                }}
              >
                <Box component="td" sx={cellSx("left")}>
                  <RankBadge rank={row.rank} t={t} />
                </Box>

                <Box component="td" sx={cellSx("left")}>
                  <Typography sx={{ fontSize: "13px", fontWeight: 500, color: t.text }}>
                    Investor - {row.unit}
                  </Typography>
                </Box>

                <Box component="td" sx={{ ...cellSx("right"), color: t.text2 }}>
                  {formatCurrency(row.invested)}
                </Box>

                <Box component="td" sx={{ ...cellSx("right"), color: t.text2 }}>
                  {row.q1Credit != null ? formatCurrency(row.q1Credit) : "-"}
                </Box>

                <Box component="td" sx={{ ...cellSx("right"), color: t.text, fontWeight: 500 }}>
                  {formatCurrency(row.current)}
                </Box>

                <Box component="td" sx={{ ...cellSx("right"), color: allTimeColor, fontWeight: 600 }}>
                  {formatSignedPercent(row.allTimeReturn)}
                </Box>

                <Box component="td" sx={{ ...cellSx("right"), color: qtrColor, fontWeight: 600 }}>
                  {formatSignedPercent(row.qtrReturn)}
                </Box>

                <Box component="td" sx={{ ...cellSx("right"), color: t.text2 }}>
                  {row.draw}%
                </Box>
              </Box>
            );
          })}
        </Box>

        {totals && (
          <Box component="tfoot">
            <Box
              component="tr"
              sx={{
                background: t.surface3,
                borderTop:  `2px solid ${t.border2}`,
              }}
            >
              <Box
                component="td"
                colSpan={2}
                sx={{ ...cellSx("left"), fontWeight: 700, color: t.text }}
              >
                League Total
              </Box>
              <Box component="td" sx={{ ...cellSx("right"), fontWeight: 700, color: t.text }}>
                {formatCurrency(totals.invested)}
              </Box>
              <Box component="td" sx={{ ...cellSx("right"), fontWeight: 700, color: t.text }}>
                {totals.q1Credit != null ? formatCurrency(totals.q1Credit) : "-"}
              </Box>
              <Box component="td" sx={{ ...cellSx("right"), fontWeight: 700, color: t.text }}>
                {formatCurrency(totals.current)}
              </Box>
              <Box
                component="td"
                sx={{
                  ...cellSx("right"),
                  fontWeight: 700,
                  color:      returnColor(totals.allTimeReturn),
                }}
              >
                {formatSignedPercent(totals.allTimeReturn)}
              </Box>
              <Box component="td" sx={cellSx("right")} />
              <Box component="td" sx={cellSx("right")} />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default LeagueStandingsTable;
